import React, { useEffect, useState, useMemo } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Modal,
  Platform,
  Alert } from 'react-native'
import { Store, Package, LayoutGrid, Armchair, MapPin, Settings, X, Check, Camera, ImagePlus, Link, ChevronRight, Map, HandCoins, ShoppingBag, Users } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'JoinMarket'>
}

interface AdminMarket {
  id: string
  name: string
  commission_percentage: number
  invite_code: string
  role: string
  store_type?: string
}

interface AvailableMarket {
  id: string
  name: string
  description: string | null
  address_city: string | null
  address_street: string | null
  commission_percentage: number
  seller_count?: number
  space_count?: number
  distance?: number
}

interface OfferAcceptingStore {
  id: string
  name: string
  description: string | null
  address_city: string | null
  address_street: string | null
  offer_dropoff_info: string | null
  distance?: number
}

interface FullServiceMarket {
  id: string
  name: string
  description: string | null
  address_city: string | null
  address_street: string | null
  commission_percentage: number
  full_service_info: string | null
  distance?: number
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  price: number
  status: string
  reservation_id: string | null
  product_images: { image_url: string }[]
}

interface ActiveReservation {
  id: string
  start_date: string
  end_date: string
  status: string
  space: {
    id: string
    name: string
    space_type: string
  }
  market: {
    id: string
    name: string
  }
  products: Product[]
}

const SPACE_TYPE_ICONS: Record<string, typeof Store> = {
  booth: Store,
  rack: LayoutGrid,
  table: Armchair,
  shelf: LayoutGrid,
  corner: LayoutGrid,
  other: Package }

export default function JoinMarketScreen({ navigation }: Props) {
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const [adminMarkets, setAdminMarkets] = useState<AdminMarket[]>([])
  const [availableMarkets, setAvailableMarkets] = useState<AvailableMarket[]>([])
  const [offerAcceptingStores, setOfferAcceptingStores] = useState<OfferAcceptingStore[]>([])
  const [fullServiceMarkets, setFullServiceMarkets] = useState<FullServiceMarket[]>([])
  const [activeReservations, setActiveReservations] = useState<ActiveReservation[]>([])
  const [unlinkedProducts, setUnlinkedProducts] = useState<Product[]>([])
  const [loadingData, setLoadingData] = useState(true)
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null)
  const [sellMode, setSellMode] = useState<'self' | 'fullservice' | 'offer' | null>(null)

  // Link modal state
  const [linkModalVisible, setLinkModalVisible] = useState(false)
  const [linkingReservationId, setLinkingReservationId] = useState<string | null>(null)
  const [selectedForLinking, setSelectedForLinking] = useState<Set<string>>(new Set())

  const getTitle = (product: Product) => {
    if (language === 'fi' && product.title_fi) return product.title_fi
    if (language === 'en' && product.title_en) return product.title_en
    return product.title || t('untitled')
  }

  // City coordinates for distance calculation
  const cityCoordinates: Record<string, { lat: number; lng: number }> = {
    'helsinki': { lat: 60.1699, lng: 24.9384 },
    'espoo': { lat: 60.2055, lng: 24.6559 },
    'vantaa': { lat: 60.2934, lng: 25.0378 },
    'tampere': { lat: 61.4978, lng: 23.7610 },
    'turku': { lat: 60.4518, lng: 22.2666 },
    'oulu': { lat: 65.0121, lng: 25.4651 },
    'pori': { lat: 61.4851, lng: 21.7973 },
    'rauma': { lat: 61.1273, lng: 21.5114 },
    'kankaanpää': { lat: 61.8047, lng: 22.3940 },
    'ulvila': { lat: 61.4293, lng: 21.8752 },
    'huittinen': { lat: 61.1767, lng: 22.6984 } }

  const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lon2 - lon1) * Math.PI / 180
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2)
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  }

  const getCityCoords = (city: string | null): { lat: number; lng: number } | null => {
    if (!city) return null
    const cityLower = city.toLowerCase()
    return cityCoordinates[cityLower] || null
  }

  const formatDistance = (distance: number | undefined): string => {
    if (distance === undefined) return ''
    return distance < 1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(1)} km`
  }

  useEffect(() => {
    // Request user location
    if (Platform.OS === 'web' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude])
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      )
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [user?.id])

  const fetchData = async () => {
    setLoadingData(true)
    try {
      let excludeIds: string[] = []
      const fleaMarketTypes = ['self_service_flea_market', 'flea_market']

      // Only fetch user-specific data if logged in
      if (user?.id) {
        // Fetch active reservations for this user
        const today = new Date().toISOString().split('T')[0]
        const { data: resData, error: resError } = await supabase
          .from('space_reservations')
          .select(`
            id,
            start_date,
            end_date,
            status,
            space:market_spaces (
              id,
              name,
              space_type
            ),
            market:flea_markets (
              id,
              name
            )
          `)
          .eq('seller_id', user.id)
          .in('status', ['pending', 'confirmed'])
          .gte('end_date', today)
          .order('start_date', { ascending: true })

        if (resError) {
          console.error('Error fetching reservations:', resError)
          setActiveReservations([])
        } else {
          // Transform reservations
          const reservationsList: ActiveReservation[] = (resData || []).map((r: any) => ({
            id: r.id,
            start_date: r.start_date,
            end_date: r.end_date,
            status: r.status,
            space: Array.isArray(r.space) ? r.space[0] : r.space,
            market: Array.isArray(r.market) ? r.market[0] : r.market,
            products: [] }))

          // Fetch all products for this user
          const { data: prodData, error: prodError } = await supabase
            .from('products')
            .select(`
              id,
              title,
              title_en,
              title_fi,
              price,
              status,
              reservation_id,
              product_images (image_url)
            `)
            .eq('seller_id', user.id)
            .order('created_at', { ascending: false })

          if (!prodError && prodData) {
            // Group products by reservation
            const unlinked: Product[] = []
            prodData.forEach((p: Product) => {
              if (p.reservation_id) {
                const res = reservationsList.find((r) => r.id === p.reservation_id)
                if (res) {
                  res.products.push(p)
                } else {
                  unlinked.push(p)
                }
              } else {
                unlinked.push(p)
              }
            })
            setUnlinkedProducts(unlinked)
          }

          setActiveReservations(reservationsList)
        }

        // Fetch markets/stores where user is admin/staff
        const { data: staffData, error: staffError } = await supabase
          .from('flea_market_staff')
          .select(`
            role,
            is_owner,
            flea_market:flea_markets (
              id,
              name,
              commission_percentage,
              invite_code,
              store_type,
              status,
              address_city
            )
          `)
          .eq('user_id', user.id)

        if (staffError) throw staffError

        const allStaff = (staffData || []).map((item: any) => {
          const market = Array.isArray(item.flea_market) ? item.flea_market[0] : item.flea_market
          return {
            id: market.id,
            name: market.name,
            commission_percentage: market.commission_percentage,
            invite_code: market.invite_code,
            role: item.role,
            store_type: market.store_type,
            status: market.status,
            address_city: market.address_city }
        }).filter((m: any) => m.id)

        // Only show flea markets (not stores) that user manages
        const fleaMarkets = allStaff.filter((m: any) => fleaMarketTypes.includes(m.store_type) || !m.store_type)
        setAdminMarkets(fleaMarkets)

        // Exclude markets user already manages
        excludeIds = fleaMarkets.map((m: any) => m.id)
      } else {
        // Not logged in - clear user-specific data
        setAdminMarkets([])
        setActiveReservations([])
        setUnlinkedProducts([])
      }

      const { data: availableData, error: availableError } = await supabase
        .from('flea_markets')
        .select(`
          id,
          name,
          description,
          address_city,
          address_street,
          commission_percentage,
          allow_self_registration
        `)
        .eq('status', 'active')
        .eq('allow_self_registration', true)
        .in('store_type', fleaMarketTypes)
        .not('id', 'in', `(${excludeIds.length > 0 ? excludeIds.join(',') : '00000000-0000-0000-0000-000000000000'})`)
        .order('name')

      if (!availableError && availableData) {
        // Get seller and space counts for each market
        const marketsWithCounts = await Promise.all(
          availableData.map(async (market) => {
            const { count: sellerCount } = await supabase
              .from('seller_market_memberships')
              .select('id', { count: 'exact', head: true })
              .eq('flea_market_id', market.id)
              .eq('status', 'approved')

            const { count: spaceCount } = await supabase
              .from('market_spaces')
              .select('id', { count: 'exact', head: true })
              .eq('flea_market_id', market.id)
              .eq('status', 'available')

            return {
              ...market,
              seller_count: sellerCount || 0,
              space_count: spaceCount || 0 }
          })
        )
        setAvailableMarkets(marketsWithCounts)
      }

      // Fetch full-service flea markets (staff handles sales on behalf of sellers)
      const { data: fullServiceData, error: fullServiceError } = await supabase
        .from('flea_markets')
        .select(`
          id,
          name,
          description,
          address_city,
          address_street,
          commission_percentage,
          full_service_info
        `)
        .eq('status', 'active')
        .eq('offers_full_service', true)
        .in('store_type', fleaMarketTypes)
        .order('name')

      if (!fullServiceError && fullServiceData) {
        setFullServiceMarkets(fullServiceData)
      }

      // Fetch stores that accept offers (buy items directly)
      const { data: offerStoresData, error: offerStoresError } = await supabase
        .from('flea_markets')
        .select(`
          id,
          name,
          description,
          address_city,
          address_street,
          offer_dropoff_info
        `)
        .eq('status', 'active')
        .eq('accepts_offers', true)
        .order('name')

      if (!offerStoresError && offerStoresData) {
        setOfferAcceptingStores(offerStoresData)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoadingData(false)
    }
  }

  // Sort markets by distance when user location is available
  const sortedAvailableMarkets = useMemo(() => {
    if (!userLocation) return availableMarkets
    return [...availableMarkets].map(market => {
      const coords = getCityCoords(market.address_city)
      const distance = coords
        ? getDistance(userLocation[0], userLocation[1], coords.lat, coords.lng)
        : undefined
      return { ...market, distance }
    }).sort((a, b) => {
      if (a.distance === undefined) return 1
      if (b.distance === undefined) return -1
      return a.distance - b.distance
    })
  }, [availableMarkets, userLocation])

  // Sort full-service markets by distance
  const sortedFullServiceMarkets = useMemo(() => {
    if (!userLocation) return fullServiceMarkets
    return [...fullServiceMarkets].map(market => {
      const coords = getCityCoords(market.address_city)
      const distance = coords
        ? getDistance(userLocation[0], userLocation[1], coords.lat, coords.lng)
        : undefined
      return { ...market, distance }
    }).sort((a, b) => {
      if (a.distance === undefined) return 1
      if (b.distance === undefined) return -1
      return a.distance - b.distance
    })
  }, [fullServiceMarkets, userLocation])

  // Sort offer-accepting stores by distance
  const sortedOfferStores = useMemo(() => {
    if (!userLocation) return offerAcceptingStores
    return [...offerAcceptingStores].map(store => {
      const coords = getCityCoords(store.address_city)
      const distance = coords
        ? getDistance(userLocation[0], userLocation[1], coords.lat, coords.lng)
        : undefined
      return { ...store, distance }
    }).sort((a, b) => {
      if (a.distance === undefined) return 1
      if (b.distance === undefined) return -1
      return a.distance - b.distance
    })
  }, [offerAcceptingStores, userLocation])

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
    const locale = language === 'fi' ? 'fi-FI' : 'en-US'
    return `${startDate.toLocaleDateString(locale, options)} - ${endDate.toLocaleDateString(locale, options)}`
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'for_sale': return '#4CAF50'
      case 'sold': return '#2196F3'
      case 'reserved': return '#FF9800'
      case 'returned': return '#F44336'
      case 'confirmed': return '#4CAF50'
      case 'pending': return '#FF9800'
      default: return '#999'
    }
  }

  const openLinkModal = (reservationId: string) => {
    setLinkingReservationId(reservationId)
    setSelectedForLinking(new Set())
    setLinkModalVisible(true)
  }

  const toggleLinkSelection = (productId: string) => {
    setSelectedForLinking((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(productId)) {
        newSet.delete(productId)
      } else {
        newSet.add(productId)
      }
      return newSet
    })
  }

  const confirmLinkProducts = async () => {
    if (!linkingReservationId || selectedForLinking.size === 0) return

    try {
      const productIds = Array.from(selectedForLinking)
      let successCount = 0

      for (const productId of productIds) {
        const { data, error } = await supabase
          .from('products')
          .update({
            reservation_id: linkingReservationId,
            status: 'for_sale'
          })
          .eq('id', productId)
          .eq('seller_id', user?.id)
          .select('id')

        if (!error && data && data.length > 0) {
          successCount++
        }
      }

      setLinkModalVisible(false)
      setSelectedForLinking(new Set())
      setLinkingReservationId(null)
      fetchData()
    } catch (error: any) {
      console.error('Error linking products:', error)
      const msg = t('link_error') || 'Failed to link products. Please try again.'
      Platform.OS === 'web' ? window.alert(msg) : Alert.alert(t('error'), msg)
    }
  }

  const unlinkProduct = async (productId: string) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ reservation_id: null, status: 'draft' })
        .eq('id', productId)

      if (error) throw error
      fetchData()
    } catch (error: any) {
      console.error('Error unlinking product:', error)
    }
  }

  const renderAdminMarket = ({ item }: { item: AdminMarket }) => (
    <TouchableOpacity
      style={styles.adminCard}
      onPress={() => navigation.navigate('MarketAdmin', { marketId: item.id })}
    >
      <View style={styles.cardContent}>
        <Text style={styles.marketName}>{item.name}</Text>
        <Text style={styles.marketDetails}>
          {item.commission_percentage}% {t('commission') || 'commission'} · {item.role}
        </Text>
      </View>
      <ChevronRight size={20} color="#999" />
    </TouchableOpacity>
  )

  if (loadingData) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{t('sell') || 'Myy'}</Text>
          <TouchableOpacity
            style={styles.mapButton}
            onPress={() => navigation.navigate('MapBrowse')}
          >
            <Map size={24} color={theme.colors.brand.primary} />
          </TouchableOpacity>
        </View>
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
</View>
    )
  }

  const renderSellModeSelector = () => (
    <View style={styles.sellModeSection}>
      {/* Option 1: Sell myself at flea market */}
      <TouchableOpacity
        style={[styles.sellModeCard, sellMode === 'self' && styles.sellModeCardActive]}
        onPress={() => setSellMode(sellMode === 'self' ? null : 'self')}
      >
        <View style={[styles.sellModeIconContainer, sellMode === 'self' && styles.sellModeIconContainerActive]}>
          <Store size={28} color={sellMode === 'self' ? '#fff' : theme.colors.brand.primary} />
        </View>
        <View style={styles.sellModeContent}>
          <Text style={[styles.sellModeTitle, sellMode === 'self' && styles.sellModeTitleActive]}>
            {t('sell_myself_at_market') || 'Haluan itse myydä kirpputorilla'}
          </Text>
          <Text style={styles.sellModeDesc}>
            {t('sell_myself_at_market_desc') || 'Varaa pöytä ja myy tuotteesi itse paikan päällä'}
          </Text>
        </View>
        <ChevronRight size={20} color={sellMode === 'self' ? theme.colors.brand.primary : '#999'} style={{ transform: [{ rotate: sellMode === 'self' ? '90deg' : '0deg' }] }} />
      </TouchableOpacity>

      {/* Option 2: Have staff sell on my behalf */}
      <TouchableOpacity
        style={[styles.sellModeCard, sellMode === 'fullservice' && styles.sellModeCardActive]}
        onPress={() => setSellMode(sellMode === 'fullservice' ? null : 'fullservice')}
      >
        <View style={[styles.sellModeIconContainer, sellMode === 'fullservice' && styles.sellModeIconContainerActive]}>
          <Users size={28} color={sellMode === 'fullservice' ? '#fff' : theme.colors.brand.primary} />
        </View>
        <View style={styles.sellModeContent}>
          <Text style={[styles.sellModeTitle, sellMode === 'fullservice' && styles.sellModeTitleActive]}>
            {t('sell_on_my_behalf') || 'Haluan että puolestani myydään'}
          </Text>
          <Text style={styles.sellModeDesc}>
            {t('sell_on_my_behalf_desc') || 'Jätä tuotteet kirpputorille ja henkilökunta hoitaa myynnin'}
          </Text>
        </View>
        <ChevronRight size={20} color={sellMode === 'fullservice' ? theme.colors.brand.primary : '#999'} style={{ transform: [{ rotate: sellMode === 'fullservice' ? '90deg' : '0deg' }] }} />
      </TouchableOpacity>

      {/* Option 3: Sell immediately to stores */}
      <TouchableOpacity
        style={[styles.sellModeCard, sellMode === 'offer' && styles.sellModeCardActive]}
        onPress={() => setSellMode(sellMode === 'offer' ? null : 'offer')}
      >
        <View style={[styles.sellModeIconContainer, sellMode === 'offer' && styles.sellModeIconContainerActive]}>
          <HandCoins size={28} color={sellMode === 'offer' ? '#fff' : theme.colors.brand.primary} />
        </View>
        <View style={styles.sellModeContent}>
          <Text style={[styles.sellModeTitle, sellMode === 'offer' && styles.sellModeTitleActive]}>
            {t('sell_immediately') || 'Haluan myydä heti'}
          </Text>
          <Text style={styles.sellModeDesc}>
            {t('sell_immediately_desc') || 'Tarjoa tavaroitasi kaupoille jotka ostavat ne heti'}
          </Text>
        </View>
        <ChevronRight size={20} color={sellMode === 'offer' ? theme.colors.brand.primary : '#999'} style={{ transform: [{ rotate: sellMode === 'offer' ? '90deg' : '0deg' }] }} />
      </TouchableOpacity>
    </View>
  )

  const renderMarketsTab = () => (
    <ScrollView style={styles.scrollContent}>
      {/* Sell Mode Selector */}
      {renderSellModeSelector()}

      {/* Active Reservations Section - Show first */}
      {activeReservations.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('my_reservations') || 'My Reservations'}</Text>
          {activeReservations.map((reservation) => (
            <View
              key={reservation.id}
              style={styles.reservationCard}
            >
              {/* Header */}
              <View style={styles.reservationHeader}>
                <View style={styles.reservationInfo}>
                  <View style={styles.spaceIconContainer}>
                    {(() => {
                      const IconComponent = SPACE_TYPE_ICONS[reservation.space?.space_type] || Package
                      return <IconComponent size={24} color={theme.colors.brand.primary} />
                    })()}
                  </View>
                  <View style={styles.reservationDetails}>
                    <Text style={styles.spaceName}>{reservation.space?.name}</Text>
                    <Text style={styles.marketNameSmall}>{reservation.market?.name}</Text>
                    <Text style={styles.dateRange}>
                      {formatDateRange(reservation.start_date, reservation.end_date)}
                    </Text>
                  </View>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(reservation.status) }]}>
                  <Text style={styles.statusText}>{t(reservation.status) || reservation.status}</Text>
                </View>
              </View>

              {/* Action Buttons - Always visible for confirmed reservations */}
              {reservation.status === 'confirmed' && (
                <View style={styles.actionButtonsGrid}>
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => navigation.navigate('Camera', { reservationId: reservation.id })}
                  >
                    <Camera size={20} color={theme.colors.brand.primary} />
                    <Text style={styles.actionButtonText}>{t('take_photos') || 'Take Photos'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => navigation.navigate('BulkUpload', { reservationId: reservation.id })}
                  >
                    <ImagePlus size={20} color={theme.colors.brand.primary} />
                    <Text style={styles.actionButtonText}>{t('from_gallery') || 'From Gallery'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => openLinkModal(reservation.id)}
                  >
                    <Link size={20} color={theme.colors.brand.primary} />
                    <Text style={styles.actionButtonText}>{t('link_products') || 'Link Products'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => navigation.navigate('ReservationSettings', { reservationId: reservation.id })}
                  >
                    <Settings size={20} color={theme.colors.brand.primary} />
                    <Text style={styles.actionButtonText}>{t('manage') || 'Manage'}</Text>
                  </TouchableOpacity>
                </View>
              )}

              {/* Linked Products */}
              {reservation.products.length > 0 ? (
                <View style={styles.linkedProductsSection}>
                  <Text style={styles.linkedProductsTitle}>
                    {t('linked_products') || 'Linked Products'} ({reservation.products.length})
                  </Text>
                  <View style={styles.linkedProductsList}>
                    {reservation.products.map((product) => (
                      <View key={product.id} style={styles.linkedProductCard}>
                        <TouchableOpacity
                          onPress={() => navigation.navigate('ProductDetail', { productId: product.id })}
                          style={styles.productTouchable}
                        >
                          <Image
                            source={{ uri: product.product_images?.[0]?.image_url || undefined }}
                            style={styles.linkedProductImage}
                          />
                          <View style={styles.linkedProductInfo}>
                            <Text style={styles.linkedProductTitle} numberOfLines={1}>{getTitle(product)}</Text>
                            <Text style={styles.linkedProductPrice}>€{product.price?.toFixed(2) || '0.00'}</Text>
                          </View>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.unlinkButton}
                          onPress={() => unlinkProduct(product.id)}
                        >
                          <X size={14} color="#999" />
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>
                </View>
              ) : (
                <View style={styles.emptyReservation}>
                  <Text style={styles.emptyReservationText}>
                    {t('no_products_yet') || 'No products yet'}
                  </Text>
                  <Text style={styles.emptyReservationHint}>
                    {t('add_products_hint') || 'Use the buttons above to add products'}
                  </Text>
                </View>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Self-sell mode: Available Markets to Browse */}
      {sellMode === 'self' && (
        <View style={styles.section}>
          {sortedAvailableMarkets.length > 0 ? (
            sortedAvailableMarkets.map((market) => (
              <View key={market.id} style={styles.availableMarketCard}>
                <View style={styles.cardContent}>
                  <View style={styles.marketNameRow}>
                    <Text style={styles.marketName}>{market.name}</Text>
                    {market.distance !== undefined && (
                      <View style={styles.distanceBadge}>
                        <MapPin size={12} color={theme.colors.text.secondary} />
                        <Text style={styles.distanceText}>{formatDistance(market.distance)}</Text>
                      </View>
                    )}
                  </View>
                  {market.address_city && (
                    <View style={styles.marketLocationRow}>
                      <MapPin size={14} color="#666" />
                      <Text style={styles.marketLocation}>{market.address_city}</Text>
                    </View>
                  )}
                  {market.description && (
                    <Text style={styles.marketDescription} numberOfLines={2}>{market.description}</Text>
                  )}
                  <View style={styles.marketStats}>
                    <Text style={styles.marketStatText}>
                      {market.commission_percentage}% {t('commission') || 'commission'}
                    </Text>
                    <Text style={styles.marketStatDivider}>·</Text>
                    <Text style={styles.marketStatText}>
                      {market.space_count} {t('spaces_available') || 'spaces'}
                    </Text>
                  </View>
                </View>
                <Button variant="primary" onPress={() => navigation.navigate('SpaceBrowse', { marketId: market.id })}><ButtonText>{t('browse_spaces') || 'Browse Spaces'}</ButtonText></Button>
              </View>
            ))
          ) : (
            <View style={styles.emptyMarketsState}>
              <Text style={styles.emptyMarketsText}>
                {t('no_markets_available') || 'No markets available in your area yet'}
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Full-service mode: Markets where staff sells on your behalf */}
      {sellMode === 'fullservice' && (
        <View style={styles.section}>
          {sortedFullServiceMarkets.length > 0 ? (
            sortedFullServiceMarkets.map((market) => (
              <View key={market.id} style={styles.availableMarketCard}>
                <View style={styles.cardContent}>
                  <View style={styles.marketNameRow}>
                    <Text style={styles.marketName}>{market.name}</Text>
                    {market.distance !== undefined && (
                      <View style={styles.distanceBadge}>
                        <MapPin size={12} color={theme.colors.text.secondary} />
                        <Text style={styles.distanceText}>{formatDistance(market.distance)}</Text>
                      </View>
                    )}
                  </View>
                  {market.address_city && (
                    <View style={styles.marketLocationRow}>
                      <MapPin size={14} color="#666" />
                      <Text style={styles.marketLocation}>{market.address_city}</Text>
                    </View>
                  )}
                  {market.description && (
                    <Text style={styles.marketDescription} numberOfLines={2}>{market.description}</Text>
                  )}
                  <View style={styles.marketStats}>
                    <View style={styles.fullServiceBadge}>
                      <Users size={14} color={theme.colors.brand.primary} />
                      <Text style={styles.fullServiceBadgeText}>{t('full_service') || 'Täyden palvelun kirpputori'}</Text>
                    </View>
                    <Text style={styles.marketStatDivider}>·</Text>
                    <Text style={styles.marketStatText}>
                      {market.commission_percentage}% {t('commission') || 'provisio'}
                    </Text>
                  </View>
                </View>
                <Button variant="primary" onPress={() => navigation.navigate('SpaceBrowse', { marketId: market.id })}><ButtonText>{t('browse_spaces') || 'Selaa paikkoja'}</ButtonText></Button>
              </View>
            ))
          ) : (
            <View style={styles.emptyMarketsState}>
              <Text style={styles.emptyMarketsText}>
                {t('no_fullservice_markets') || 'Ei täyden palvelun kirpputoreja alueellasi vielä'}
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Offer mode: Stores that accept offers */}
      {sellMode === 'offer' && (
        <View style={styles.section}>
          {sortedOfferStores.length > 0 ? (
            sortedOfferStores.map((store) => (
              <View key={store.id} style={styles.availableMarketCard}>
                <View style={styles.cardContent}>
                  <View style={styles.marketNameRow}>
                    <Text style={styles.marketName}>{store.name}</Text>
                    {store.distance !== undefined && (
                      <View style={styles.distanceBadge}>
                        <MapPin size={12} color={theme.colors.text.secondary} />
                        <Text style={styles.distanceText}>{formatDistance(store.distance)}</Text>
                      </View>
                    )}
                  </View>
                  {store.address_city && (
                    <View style={styles.marketLocationRow}>
                      <MapPin size={14} color="#666" />
                      <Text style={styles.marketLocation}>{store.address_city}</Text>
                    </View>
                  )}
                  {store.description && (
                    <Text style={styles.marketDescription} numberOfLines={2}>{store.description}</Text>
                  )}
                  <View style={styles.offerBadge}>
                    <HandCoins size={14} color={theme.colors.brand.primary} />
                    <Text style={styles.offerBadgeText}>{t('accepts_offers') || 'Ottaa vastaan tarjouksia'}</Text>
                  </View>
                </View>
                <Button variant="primary" onPress={() => navigation.navigate('SubmitOffer', { storeId: store.id })}><ButtonText>{t('submit_offer') || 'Tee tarjous'}</ButtonText></Button>
              </View>
            ))
          ) : (
            <View style={styles.emptyMarketsState}>
              <Text style={styles.emptyMarketsText}>
                {t('no_stores_accepting_offers') || 'Ei kauppoja jotka ottavat vastaan tarjouksia'}
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Admin Markets Section */}
      {adminMarkets.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('my_managed_markets') || 'Markets I Manage'}</Text>
          {adminMarkets.map((market) => (
            <TouchableOpacity
              key={market.id}
              style={styles.adminCard}
              onPress={() => navigation.navigate('MarketAdmin', { marketId: market.id })}
            >
              <View style={styles.cardContent}>
                <Text style={styles.marketName}>{market.name}</Text>
                <Text style={styles.marketDetails}>
                  {market.commission_percentage}% · {market.role}
                </Text>
              </View>
              <ChevronRight size={20} color="#999" />
            </TouchableOpacity>
          ))}
        </View>
      )}

    </ScrollView>
  )

  const renderLinkModal = () => (
    <Modal
      visible={linkModalVisible}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setLinkModalVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.linkModalContent}>
          <View style={styles.linkModalHeader}>
            <Text style={styles.linkModalTitle}>{t('link_products') || 'Link Products'}</Text>
            <TouchableOpacity onPress={() => setLinkModalVisible(false)}>
              <X size={24} color="#999" />
            </TouchableOpacity>
          </View>

          {unlinkedProducts.length > 0 ? (
            <>
              <Text style={styles.linkModalHint}>
                {t('select_products_to_link') || 'Select products to link to this reservation'}
              </Text>
              <ScrollView style={styles.linkModalList}>
                {unlinkedProducts.map((product) => {
                  const isSelected = selectedForLinking.has(product.id)
                  return (
                    <TouchableOpacity
                      key={product.id}
                      style={[styles.linkModalItem, isSelected && styles.linkModalItemSelected]}
                      onPress={() => toggleLinkSelection(product.id)}
                    >
                      <Image
                        source={{ uri: product.product_images?.[0]?.image_url || undefined }}
                        style={styles.linkModalItemImage}
                      />
                      <View style={styles.linkModalItemInfo}>
                        <Text style={styles.linkModalItemTitle} numberOfLines={1}>
                          {getTitle(product)}
                        </Text>
                        <Text style={styles.linkModalItemPrice}>€{product.price?.toFixed(2)}</Text>
                      </View>
                      <View style={[styles.linkCheckbox, isSelected && styles.linkCheckboxSelected]}>
                        {isSelected && <Check size={16} color="#fff" />}
                      </View>
                    </TouchableOpacity>
                  )
                })}
              </ScrollView>
              <TouchableOpacity
                style={[
                  styles.linkConfirmButton,
                  selectedForLinking.size === 0 && styles.linkConfirmButtonDisabled,
                ]}
                onPress={confirmLinkProducts}
                disabled={selectedForLinking.size === 0}
              >
                <Text style={styles.linkConfirmButtonText}>
                  {t('link')} {selectedForLinking.size > 0 ? `(${selectedForLinking.size})` : ''}
                </Text>
              </TouchableOpacity>
            </>
          ) : (
            <View style={styles.linkModalEmpty}>
              <Text style={styles.linkModalEmptyText}>
                {t('no_products_to_link') || 'No products available to link'}
              </Text>
              <Text style={styles.linkModalEmptyHint}>
                {t('add_products_first') || 'Add products using the camera or gallery first'}
              </Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  )

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('sell') || 'Myy'}</Text>
        <TouchableOpacity
          style={styles.mapButton}
          onPress={() => navigation.navigate('MapBrowse')}
        >
          <Map size={24} color={theme.colors.brand.primary} />
        </TouchableOpacity>
      </View>

      {renderMarketsTab()}
      {renderLinkModal()}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingTop: 20,
    paddingBottom: 16,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  headerTitle: {
    fontSize: 32,
    fontFamily: '$cursive',
    color: theme.colors.brand.primary },
  mapButton: {
    padding: 8,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: `${theme.colors.brand.primary}15` },
  scrollContent: {
    flex: 1 },
  sellModeSection: {
    padding: theme.spacing.md,
    gap: 12 },
  sellModeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  sellModeCardActive: {
    borderColor: theme.colors.brand.primary,
    backgroundColor: `${theme.colors.brand.primary}08` },
  sellModeIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: `${theme.colors.brand.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14 },
  sellModeIconContainerActive: {
    backgroundColor: theme.colors.brand.primary },
  sellModeContent: {
    flex: 1 },
  sellModeTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  sellModeTitleActive: {
    color: theme.colors.brand.primary },
  sellModeDesc: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    lineHeight: 18 },
  offerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: `${theme.colors.brand.primary}15`,
    borderRadius: theme.borderRadius.full,
    alignSelf: 'flex-start' },
  offerBadgeText: {
    fontSize: 12,
    color: theme.colors.brand.primary,
    fontWeight: '500',
    marginLeft: 4 },
  fullServiceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4 },
  fullServiceBadgeText: {
    fontSize: 12,
    color: theme.colors.brand.primary,
    fontWeight: '500' },
  section: {
    backgroundColor: theme.colors.background.card,
    padding: theme.spacing.md,
    marginTop: theme.spacing.md },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md },
  adminCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${theme.colors.brand.primary}15`,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.sm,
    marginBottom: theme.spacing.sm,
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.brand.primary },
  membershipCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.sm,
    marginBottom: theme.spacing.sm },
  cardContent: {
    flex: 1 },
  marketNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4 },
  marketName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    flex: 1 },
  distanceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${theme.colors.brand.primary}15`,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.full,
    marginLeft: 8 },
  distanceText: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginLeft: 4,
    fontWeight: '500' },
  marketDetails: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  chevron: {
    fontSize: 20,
    color: theme.colors.brand.primary,
    fontWeight: '600' },
  statusBadge: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: 6,
    borderRadius: theme.borderRadius.md },
  statusText: {
    color: theme.colors.text.inverse,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize' },
  browseSpacesButton: {
    marginTop: theme.spacing.sm,
    backgroundColor: `${theme.colors.status.success}15`,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: 6,
    borderRadius: theme.borderRadius.full,
    alignSelf: 'flex-start' },
  browseSpacesText: {
    color: theme.colors.status.success,
    fontSize: 13,
    fontWeight: '500' },
  availableMarketCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  marketLocationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2 },
  marketLocation: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginLeft: 4 },
  marketDescription: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginTop: 4,
    lineHeight: 18 },
  marketStats: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8 },
  marketStatText: {
    fontSize: 12,
    color: theme.colors.brand.primary,
    fontWeight: '500' },
  marketStatDivider: {
    marginHorizontal: 6,
    color: theme.colors.ui.border },
  browseButton: {
    backgroundColor: theme.colors.brand.primary,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: 10,
    borderRadius: theme.borderRadius.sm },
  browseButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600' },
  emptyMarketsState: {
    padding: theme.spacing.xl,
    alignItems: 'center' },
  emptyMarketsText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'center' },
  // Reservation card styles
  reservationCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  reservationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start' },
  reservationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1 },
  spaceIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: `${theme.colors.brand.primary}15`,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12 },
  reservationDetails: {
    flex: 1 },
  spaceName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  marketNameSmall: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  dateRange: {
    fontSize: 12,
    color: theme.colors.brand.primary,
    marginTop: 4,
    fontWeight: '500' },
  reservationActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border },
  productCountBadge: {
    backgroundColor: theme.colors.background.secondary,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.sm },
  productCountText: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    fontWeight: '500' },
  actionButtonsRow: {
    flexDirection: 'row',
    gap: 8 },
  actionButtonSmall: {
    backgroundColor: theme.colors.background.secondary,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.sm },
  actionButtonSmallText: {
    fontSize: 13,
    color: theme.colors.text.primary,
    fontWeight: '500' },
  // Action buttons grid
  actionButtonsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 16,
    marginBottom: 16 },
  actionButton: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  actionButtonIcon: {
    fontSize: 24,
    marginBottom: 6 },
  actionButtonText: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    fontWeight: '500',
    textAlign: 'center' },
  // Linked products
  linkedProductsSection: {
    marginTop: 8 },
  linkedProductsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.secondary,
    marginBottom: 10 },
  linkedProductsList: {
    gap: 8 },
  linkedProductCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: 10,
    padding: 8 },
  productTouchable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center' },
  linkedProductImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
    backgroundColor: theme.colors.ui.border },
  linkedProductInfo: {
    flex: 1,
    marginLeft: 12 },
  linkedProductTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  linkedProductPrice: {
    fontSize: 13,
    color: theme.colors.brand.primary,
    fontWeight: '600',
    marginTop: 2 },
  unlinkButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#FFEBEE',
    justifyContent: 'center',
    alignItems: 'center' },
  unlinkButtonText: {
    color: '#F44336',
    fontSize: 14,
    fontWeight: '600' },
  // Empty reservation state
  emptyReservation: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    marginTop: 12 },
  emptyReservationText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: 4 },
  emptyReservationHint: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  // Link modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  linkModalContent: {
    backgroundColor: theme.colors.background.card,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    maxHeight: '80%' },
  linkModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12 },
  linkModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  linkModalClose: {
    fontSize: 24,
    color: theme.colors.text.secondary,
    padding: 4 },
  linkModalHint: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: 16 },
  linkModalList: {
    maxHeight: 400 },
  linkModalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderRadius: 10,
    marginBottom: 8,
    backgroundColor: theme.colors.background.secondary },
  linkModalItemSelected: {
    backgroundColor: 'rgba(116, 140, 112, 0.15)',
    borderWidth: 1,
    borderColor: theme.colors.brand.primary },
  linkModalItemImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
    backgroundColor: theme.colors.ui.border },
  linkModalItemInfo: {
    flex: 1,
    marginLeft: 12 },
  linkModalItemTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  linkModalItemPrice: {
    fontSize: 13,
    color: theme.colors.brand.primary,
    fontWeight: '600',
    marginTop: 2 },
  linkCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    backgroundColor: theme.colors.background.card,
    justifyContent: 'center',
    alignItems: 'center' },
  linkCheckboxSelected: {
    backgroundColor: theme.colors.brand.primary,
    borderColor: theme.colors.brand.primary },
  linkCheckmark: {
    color: theme.colors.text.inverse,
    fontWeight: 'bold',
    fontSize: 14 },
  linkConfirmButton: {
    backgroundColor: theme.colors.brand.primary,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 16 },
  linkConfirmButtonDisabled: {
    opacity: 0.5 },
  linkConfirmButtonText: {
    color: theme.colors.text.inverse,
    fontWeight: '600',
    fontSize: 16 },
  linkModalEmpty: {
    alignItems: 'center',
    paddingVertical: 32 },
  linkModalEmptyText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    marginBottom: 8 },
  linkModalEmptyHint: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'center' } })
